import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/supabase-context';
import Loading from '../common/Loading';

const ProtectedRoute = ({ children, requiredRole = null, requiredRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingRole, setCheckingRole] = useState(false);
  const { getUserRole, hasAnyRole } = useAuth();

  useEffect(() => {
    const checkUserAccess = async () => {
      if (!user) return;
      
      setCheckingRole(true);
      
      try {
        // First check if we have a cached role in localStorage
        const cachedRole = localStorage.getItem(`userRole_${user.id}`);
        
        // If specific roles are required
        if (requiredRoles && requiredRoles.length > 0) {
          // Try to use cached role first if available
          if (cachedRole) {
            // Admin has access to everything
            if (cachedRole === 'Admin') {
              setHasAccess(true);
            } else {
              setHasAccess(requiredRoles.includes(cachedRole));
            }
          } else {
            // Fall back to API call
            const hasRole = await hasAnyRole(requiredRoles);
            setHasAccess(hasRole);
          }
        }
        // If a single role is required
        else if (requiredRole) {
          // Try to use cached role first if available
          if (cachedRole) {
            // Admin has access to everything
            setHasAccess(cachedRole === 'Admin' || cachedRole === requiredRole);
          } else {
            try {
              const role = await getUserRole();
              // Admin has access to everything
              setHasAccess(role === 'Admin' || role === requiredRole);
            } catch (error) {
              console.error("Error getting user role:", error);
              // Log more details about the error
              console.error("Error details:", JSON.stringify(error));

              // SECURITY: Always deny access on error (fail-safe)
              // Never auto-grant access, even in development
              console.warn("Denying access due to role check error");
              setHasAccess(false);
            }
          }
        } 
        // No role requirements
        else {
          setHasAccess(true);
        }
      } catch (error) {
        console.error("Error checking user access:", error);
        // Log more details about the error
        console.error("Error details:", JSON.stringify(error));

        // SECURITY: Always deny access on error (fail-safe)
        // Never auto-grant access, even in development
        console.warn("Denying access due to access check error");
        setHasAccess(false);
      } finally {
        setCheckingRole(false);
      }
    };

    checkUserAccess();
  }, [user, requiredRole, requiredRoles, getUserRole, hasAnyRole]);

  if (loading || checkingRole) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if ((requiredRole || requiredRoles.length > 0) && hasAccess === false) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
